import { AssetTypeCode } from '@atlas/types'
import { CardShell, EmptyState, SectionCard } from '@atlas/ui-components'
import { Badge, Button, Checkbox, Chip, Combobox, IconButton, Input } from '@atlas/ui-primitives'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { extractApiErrorMessage } from '@/adapters/workflow-admin-http-error'
import { Eyebrow, PageHeader } from '@/components/editorial/PageHeader'
import {
	useChecklistTemplate,
	useChecklistTemplates,
	useCreateChecklistTemplate,
	useCreateChecklistTemplateItem,
	useDeleteChecklistTemplate,
	useDeleteChecklistTemplateItem,
} from '@/hooks/useChecklistTemplateAdmin'

const ASSET_TYPE_OPTIONS = Object.values(AssetTypeCode).map((code) => ({ value: code, label: code }))

/** New-template form — code/label/note + an asset-type matcher built one code at a time (absent = "any asset type"). */
function NewTemplateForm() {
	const createTemplate = useCreateChecklistTemplate()
	const [code, setCode] = useState('')
	const [label, setLabel] = useState('')
	const [note, setNote] = useState('')
	const [assetTypeCodes, setAssetTypeCodes] = useState<AssetTypeCode[]>([])
	const [pendingAssetType, setPendingAssetType] = useState<string | undefined>(undefined)

	const submit = () => {
		if (!code || !label) return
		createTemplate.mutate(
			{ code, label, note: note || null, assetTypeCodes: assetTypeCodes.length ? assetTypeCodes : null },
			{
				onSuccess: () => {
					setCode('')
					setLabel('')
					setNote('')
					setAssetTypeCodes([])
				},
			},
		)
	}

	return (
		<SectionCard title="New template" className="p-4">
			<div className="space-y-3">
				<div className="flex flex-wrap gap-3">
					<Input
						aria-label="Code"
						placeholder="production"
						value={code}
						onChange={(e) => setCode(e.target.value)}
						className="max-w-40"
					/>
					<Input
						aria-label="Label"
						placeholder="Production checklist"
						value={label}
						onChange={(e) => setLabel(e.target.value)}
						className="max-w-xs"
					/>
					<Input
						aria-label="Note"
						placeholder="resolved template: previo/social/LatAm-EU"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						className="max-w-sm"
					/>
				</div>
				<div>
					<Eyebrow className="mb-1">Asset-type matcher (absent = any asset type)</Eyebrow>
					<div className="flex flex-wrap items-center gap-2">
						{assetTypeCodes.map((atc) => (
							<Chip
								key={atc}
								variant="muted"
								size="sm"
								onRemove={() => setAssetTypeCodes((prev) => prev.filter((c) => c !== atc))}
								removeLabel={`Remove ${atc}`}
							>
								{atc}
							</Chip>
						))}
						<Combobox
							aria-label="Add an asset type"
							options={ASSET_TYPE_OPTIONS.filter((o) => !assetTypeCodes.includes(o.value))}
							value={pendingAssetType}
							onChange={setPendingAssetType}
							placeholder="Asset type…"
						/>
						<Button
							size="sm"
							variant="secondary"
							isDisabled={!pendingAssetType}
							onPress={() => {
								if (!pendingAssetType) return
								setAssetTypeCodes((prev) => [...prev, pendingAssetType as AssetTypeCode])
								setPendingAssetType(undefined)
							}}
						>
							Add
						</Button>
					</div>
				</div>
				{createTemplate.isError && (
					<p className="text-sm text-ds-danger">{extractApiErrorMessage(createTemplate.error)}</p>
				)}
				<Button onPress={submit} isDisabled={createTemplate.isPending || !code || !label}>
					{createTemplate.isPending ? 'Creating…' : 'Create template'}
				</Button>
			</div>
		</SectionCard>
	)
}

/** A selected template's item list + a small add-item form. */
function TemplateItemsPanel({ templateId }: { templateId: string }) {
	const { data: template } = useChecklistTemplate(templateId)
	const createItem = useCreateChecklistTemplateItem()
	const deleteItem = useDeleteChecklistTemplateItem()
	const [itemLabel, setItemLabel] = useState('')
	const [itemTag, setItemTag] = useState('')
	const [required, setRequired] = useState(true)
	const [autoChecked, setAutoChecked] = useState(false)

	if (!template) return null

	const submitItem = () => {
		if (!itemLabel) return
		createItem.mutate(
			{ templateId, label: itemLabel, tag: itemTag || null, required, position: template.items.length, autoChecked },
			{
				onSuccess: () => {
					setItemLabel('')
					setItemTag('')
					setRequired(true)
					setAutoChecked(false)
				},
			},
		)
	}

	return (
		<SectionCard title={`${template.label} — items`} className="bg-ds-surface p-4">
			<div className="space-y-3">
				{template.items.length > 0 ? (
					<ul className="space-y-2">
						{template.items.map((item) => (
							<li key={item.itemId} className="flex items-center justify-between gap-2 text-sm">
								<span>
									{item.label}
									{item.tag ? (
										<Badge variant="muted" size="sm" className="ml-2">
											{item.tag}
										</Badge>
									) : null}
									{!item.required ? <span className="ml-2 text-ds-text-subtle">(optional)</span> : null}
									{item.autoChecked ? <span className="ml-2 text-ds-text-subtle">(auto-checked)</span> : null}
								</span>
								<IconButton
									size="sm"
									variant="muted"
									aria-label={`Archive ${item.label}`}
									onPress={() => deleteItem.mutate({ templateId, itemId: item.itemId })}
								>
									<Trash2 className="h-3.5 w-3.5" />
								</IconButton>
							</li>
						))}
					</ul>
				) : (
					<EmptyState title="No items yet." />
				)}

				<div className="flex flex-wrap items-end gap-2 border-t border-ds-border pt-3">
					<Input
						aria-label="Item label"
						placeholder="Check brand guidelines"
						value={itemLabel}
						onChange={(e) => setItemLabel(e.target.value)}
						className="max-w-xs"
					/>
					<Input
						aria-label="Tag"
						placeholder="IMPORTANTE"
						value={itemTag}
						onChange={(e) => setItemTag(e.target.value)}
						className="max-w-40"
					/>
					<Checkbox size="sm" isSelected={required} onChange={setRequired}>
						Required
					</Checkbox>
					<Checkbox size="sm" isSelected={autoChecked} onChange={setAutoChecked}>
						Auto-checked
					</Checkbox>
					<Button size="sm" onPress={submitItem} isDisabled={createItem.isPending || !itemLabel}>
						Add item
					</Button>
				</div>
			</div>
		</SectionCard>
	)
}

/** The checklist template library — create/archive templates and manage their items. */
export function ChecklistTemplateLibraryPage() {
	const { data: templates = [] } = useChecklistTemplates()
	const deleteTemplate = useDeleteChecklistTemplate()
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)

	return (
		<div className="space-y-6">
			<PageHeader
				eyebrow="Admin · Workflow & checklists"
				title="Checklist template library"
				subtitle="Create, edit, and archive checklist templates and their items. Archiving preserves every past execution."
			/>

			<NewTemplateForm />

			<div>
				<Eyebrow className="mb-2">Templates ({templates.length})</Eyebrow>
				<ul className="space-y-2">
					{templates.map((t) => (
						<CardShell
							as="li"
							key={t.templateId}
							className="flex items-center justify-between gap-2 bg-transparent p-3"
						>
							<button type="button" className="text-left" onClick={() => setSelectedTemplateId(t.templateId)}>
								<span className="font-medium text-ds-text">{t.label}</span>
								<span className="ml-2 font-mono text-xs text-ds-text-subtle">{t.code}</span>
								{t.assetTypeCodes ? (
									<span className="ml-2 text-xs text-ds-text-subtle">{t.assetTypeCodes.join(', ')}</span>
								) : (
									<span className="ml-2 text-xs text-ds-text-subtle">any asset type</span>
								)}
							</button>
							<IconButton
								size="sm"
								variant="muted"
								aria-label={`Archive ${t.label}`}
								onPress={() => deleteTemplate.mutate(t.templateId)}
							>
								<Trash2 className="h-3.5 w-3.5" />
							</IconButton>
						</CardShell>
					))}
				</ul>
			</div>

			{selectedTemplateId && <TemplateItemsPanel templateId={selectedTemplateId} />}
		</div>
	)
}
